import {EventEmitter, Injectable} from '@angular/core';
import { io, Socket } from 'socket.io-client';
import { Observable } from 'rxjs';
import {ServerService} from "./ServerService";
import {HttpClient} from "@angular/common/http";
import {JobImpressaoService} from "./job-impressao.service";

@Injectable({
  providedIn: 'root'
})
export class SocketService extends ServerService {
  private socket: Socket;
  private tabId: string;
  // Master de impressão (server-driven via Redis)
  private tabServidorId: number;
  private tabMasterToken: string;
  private tabMasterTtlMs = 15000;
  private tabMasterLoopId: any;
  private tabMaster = false;

  onImpressaoMasterChanged = new EventEmitter<boolean>();
  conectado = false;
  desconectou = false;
  onReconectou = new EventEmitter();
  constructor(protected http: HttpClient, private jobImpressaoService: JobImpressaoService) {
    super(http);
    // Gerar ID único para esta aba
    this.tabId = this.gerarTabId();

    const host = window.location.hostname;
    let socketUrl = `https://${host}`;
    const isLocal = host === 'localhost' || host === '127.0.0.1';

    let ops: any =  {
      transports: ['websocket'],
      reconnection: true,
      reconnectionDelay: 2000,
      timeout: 5000,
      pingInterval: 5000,
      pingTimeout: 3000,
      query: {
        tabId: this.tabId,
        timestamp: Date.now()
      }
    }

    if(isLocal){
      socketUrl =  (window.location.protocol === 'https:' ? 'wss://localhost:3000' : 'ws://localhost:3000')
      ops.rejectUnauthorized = false;
    } else {
      ops.path =  '/testesocketio'
    }

    console.log(socketUrl)
    console.log(ops)

    this.socket = io(socketUrl, ops);

    this.on('reload').subscribe(() => {
      window.location.reload();
      window.parent.postMessage({tipo: "NOVA_MENSAGEM", text: {tipo: 'RELOAD'}}, "*");
    });

    this.on('connect').subscribe(() => {
      console.log('[SOCKET] connect - Tab:', this.tabId);
      this.conectado = true;
      if(this.desconectou)
        this.onReconectou.emit()
    });

    this.on('connect_timeout').subscribe(() => {
      console.warn('[SOCKET] timeout ao conectar');
      this.conectado = false;
    });

    this.on('disconnect').subscribe((reason) => {
      console.warn('[SOCKET] disconnect:', reason);
      this.conectado = false;
      this.desconectou = true;
    });

    this.on('connect_error').subscribe((err) => {
      console.error('[SOCKET] connect_error:');
      console.error(err)
      if(this.conectado)
        this.desconectou = true;

      this.conectado = false;
    });

    this.on('reconnect_attempt').subscribe(() => {
      console.log('[SOCKET] reconnect_attempt');
    });

    this.on('reconnect_failed').subscribe(() => {
      console.error('[SOCKET] falhou ao tentar reconectar');
    });

  }

  private gerarTabId(): string {
    // Persistir o tabId no sessionStorage para sobreviver a reloads (F5)
    const existente = sessionStorage.getItem('socket_tab_id');
    if (existente) {
      return existente;
    }
    const novo = `tab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('socket_tab_id', novo);
    return novo;
  }

  private getOrCreateImpressaoMasterToken(servidorId: number): string {
    // Token único por aba (sessionStorage), usado apenas para o lock de impressão no servidor.
    const key = `mc_print_master_token_${servidorId}`;
    try {
      const existente = sessionStorage.getItem(key);
      if (existente) return existente;
      const novo = `pm_${Date.now()}_${Math.random().toString(36).substr(2, 10)}`;
      sessionStorage.setItem(key, novo);
      return novo;
    } catch (_) {
      return `pm_${Date.now()}_${Math.random().toString(36).substr(2, 10)}`;
    }
  }

  isTabMaster(): boolean {
    return this.tabMaster;
  }

  private setImpressaoMaster(novo: boolean) {
    const mudou = this.tabMaster !== novo;
    this.tabMaster = novo;
    if (mudou) this.onImpressaoMasterChanged.emit(novo);
  }

  async tenteAssumirMasterImpressao(servidorId?: number): Promise<boolean> {
    const id = servidorId || this.tabServidorId;
    if (!id) return false;
    if (!this.tabMasterToken) this.tabMasterToken = this.getOrCreateImpressaoMasterToken(id);
    try {
      const resp: any = await this.jobImpressaoService.claimMaster(id,  this.tabMasterToken, this.tabMasterTtlMs);
      this.setImpressaoMaster(!!(resp && resp.master));
      return this.tabMaster;
    } catch (e) {
      console.error('Erro ao tentar assumir master de impressão:', e);
      this.setImpressaoMaster(false);
      return false;
    }
  }

  async renoveMasterImpressao(): Promise<boolean> {
    const id = this.tabServidorId;
    if (!id || !this.tabMasterToken) return false;
    try {
      const resp: any = await this.jobImpressaoService.renewMaster(id,  this.tabMasterToken, this.tabMasterTtlMs);
      this.setImpressaoMaster(!!(resp && resp.master));
      return this.tabMaster;
    } catch (e) {
      console.error('Erro ao renovar master de impressão:', e);
      this.setImpressaoMaster(false);
      return false;
    }
  }

  iniciarMasterImpressao(servidorId: number, ttlMs: number = 15000): Promise<void> {
    this.tabServidorId = servidorId;
    this.tabMasterTtlMs = ttlMs;
    if (!this.tabMasterToken) this.tabMasterToken = this.getOrCreateImpressaoMasterToken(servidorId);

    if (this.tabMasterLoopId) return Promise.resolve();

    return new Promise(async (resolve) => {
      await this.tenteAssumirMasterImpressao(servidorId);

      this.tabMasterLoopId = setInterval(async () => {
        if (!this.tabServidorId) return;
        if (this.tabMaster) await this.renoveMasterImpressao();
        else await this.tenteAssumirMasterImpressao(this.tabServidorId);
      }, 5000);
      resolve();
    });
  }

  async pararMasterImpressao(liberar: boolean = true): Promise<void> {
    if (this.tabMasterLoopId) {
      clearInterval(this.tabMasterLoopId);
      this.tabMasterLoopId = null;
    }

    // Em teardown/refresh: não liberar (deixa expirar). Em parada explícita: liberar.
    if (liberar && this.tabServidorId && this.tabMasterToken) {
      try {
        await this.jobImpressaoService.releaseMaster(this.tabServidorId, this.tabMasterToken);
      } catch (e) {
        console.warn('Falha ao liberar master de impressão (best-effort):', e);
      }
    }

    this.setImpressaoMaster(false);
  }

  emit(eventName: string, data: any): void {
    this.socket.emit(eventName, data);
  }

  atualizeAbas() {
    return this.obtenha('/sockets/atualizeAbas', {});
  }

  // Método para ouvir eventos do servidor
  on(eventName: string): Observable<any> {
    return new Observable<any>(observer => {
      this.socket.on(eventName, (data: any) => {
        observer.next(data);
      });

      // Limpeza quando o observable é destruído
      return () => this.socket.off(eventName);
    });
  }

  // Método público para forçar reconexão (útil para debug)
  forcarReconexao(): void {
    this.socket.disconnect();
    setTimeout(() => {
      this.socket.connect();
    }, 1000);
  }


}
